import { ValidationErrors, Validators } from '@angular/forms';
import { NgClass, NgStyle } from '@angular/common';
import {
  Component,
  OnInit,
  InputSignal,
  input,
  ChangeDetectionStrategy,
  OutputEmitterRef,
  output,
  inject,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { Tooltip } from '../../directives/tooltip.directive';

@Component({
  selector: 'app-input-element',
  imports: [
    NgClass,
    TranslateModule,
    UpperFirstLetterPipe,
    ReactiveFormsModule,
    IconComponent,
    Tooltip,
    NgStyle,
  ],
  templateUrl: './input-element.html',
  styleUrl: './input-element.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputElement implements OnInit {
  private readonly translateService: TranslateService = inject(TranslateService);
  readonly label: InputSignal<string> = input.required<string>();
  readonly inputId: InputSignal<string> = input.required<string>();
  readonly maxlength: InputSignal<number> = input<number>(128);
  readonly errorMessages: InputSignal<{ [key: string]: string }> = input({});
  readonly showCounter: InputSignal<boolean> = input<boolean>(true);
  readonly tooltip: InputSignal<string | undefined> = input<string | undefined>(undefined);
  readonly inputFormControl: InputSignal<FormControl> = input.required<FormControl>();

  valueChange: OutputEmitterRef<string> = output<string>();

  focused: boolean = false;
  touched: boolean = false;
  public validatorsRequired = Validators.required;

  get counterThreshold(): number {
    return this.maxlength() - this.maxlength() / 3;
  }

  ngOnInit() {
    if (this.inputFormControl()) {
      this.inputFormControl().valueChanges.subscribe((value) => {
        this.valueChange.emit(value ?? '');
      });
    }
  }

  onBlur() {
    this.focused = false;
    let value = '';
    if (this.inputFormControl() && typeof this.inputFormControl().value === 'string') {
      value = this.inputFormControl().value.trim();
    }
    if (value === '') {
      this.touched = false;
    } else {
      this.touched = true;
    }
  }

  get hasValue(): boolean {
    const value: unknown = this.inputFormControl()?.value;
    return typeof value === 'string' && value.trim() !== '';
  }

  get hasError(): boolean {
    if (!this.inputFormControl()) return false;
    const ctrl = this.inputFormControl();
    if (ctrl.errors?.['maxlength']) return true;
    return ctrl.invalid && (this.touched || ctrl.touched);
  }

  get inputValueLength(): number {
    return this.inputFormControl()?.value?.length || 0;
  }

  getErrorMessage(): string | null {
    if (!this.inputFormControl() || !this.inputFormControl().errors) return null;

    const errorKey: string = Object.keys(this.inputFormControl().errors!)[0];
    const errorValue: ValidationErrors = this.inputFormControl().errors![errorKey];

    if (this.errorMessages()[errorKey]) {
      return this.errorMessages()[errorKey];
    }

    switch (errorKey) {
      case 'required':
        return this.translateService.instant('input_element.error.required');
      case 'minlength':
        return this.translateService.instant('input_element.error.minlength', {
          length: errorValue['requiredLength'],
        });
      case 'maxlength':
        return this.translateService.instant('input_element.error.maxlength', {
          current: this.inputValueLength,
          max: errorValue['requiredLength'],
        });
      case 'invalidChar':
        return this.translateService.instant('input_element.error.invalid_char', {
          char: errorValue,
        });
      default:
        return this.translateService.instant('input_element.error.invalid');
    }
  }
}
